// Surface-glued panel placement.
//
// A treatment panel is NOT a free rigid body. Physically it is fixed to a
// surface — a wall, the ceiling, the floor, or straddling a vertical corner —
// and the only things a user can actually change are:
//
//   • where it sits ON that surface        (u, v)
//   • how it is turned IN the surface plane (spin — portrait vs landscape)
//   • how far it stands OFF the surface     (airGap, on PanelEntry)
//
// This module is the pure (no-React, no-r3f) mapping between that 3-DOF
// surface parametrisation and the doc's world-space `position` + `rotation`.
//
// Frames are built so the emitted rotation matches the BACKEND's convention
// exactly (admin_room_treatment._placement_to_admin_box):
//
//   panel local +Z = the room-facing normal   (airgap displaces along it)
//   panel local +X = horizontal, along the surface
//   panel local +Y = up (walls) / in-plane (ceiling, floor)
//
// Verified against every axis-aligned case the solver emits: front (0,π/2,0),
// back (0,-π/2,0), left (0,0,0), right (0,π,0), ceiling (π/2,0,0), floor
// (-π/2,0,0), and the four 45° corner yaws. Building the basis generically
// reproduces all of them, so a hand-placed panel and a solver-placed panel are
// the same kind of object.
//
// Coordinate frame: Y up, floor at y=0. The roomGeometry polygon uses
// PolyPt {x, y} where `y` is the WIDTH axis = world Z.

import * as THREE from 'three';
import type { PanelSize, Vec3 } from '@roomtreatment/core';
import type { EditorDoc, PanelEntry, WindowEntry } from '@roomtreatment/core';
import { cornerWedgeVertices, isConvexCorner, interiorAngleAt, interiorAngleForPanel } from '@roomtreatment/core';
import { getRoomPolygon } from './roomPolygon';
import { clampBoxToRoom, pointInPolygon } from './polygonClamp';
import type { PolyPt } from '@roomtreatment/floorplan-core';
import { doorsOn } from './doorOpening';

const UP = new THREE.Vector3(0, 1, 0);

/** Walls shorter than this are structural slivers (a wing's return edge, a
 *  chamfer) — not somewhere a user would mount a panel. Skipped. */
const MIN_WALL_LEN = 0.35;

export type SurfaceKind = 'wall' | 'ceiling' | 'floor' | 'corner';

/** Identifies one mounting surface. `index` is the room-polygon edge index for
 *  'wall', the vertex index for 'corner', and unused (0) for ceiling / floor. */
export interface SurfaceRef {
  kind: SurfaceKind;
  index: number;
}

/** A mounting surface with its in-plane basis. `(u, v, n)` is right-handed, so
 *  `makeBasis(u, v, n)` is a proper rotation and local +Z lands on `n`. */
export interface SurfaceFrame {
  ref: SurfaceRef;
  /** Human label — listener-relative ('Front wall' = the wall you face). */
  label: string;
  /** World point at (u = 0, v = 0) ON the surface (no panel-depth offset). */
  origin: THREE.Vector3;
  u: THREE.Vector3;
  v: THREE.Vector3;
  /** Unit normal pointing INTO the room. */
  n: THREE.Vector3;
  uMax: number;
  vMax: number;
  /** Interior angle (radians) at this vertex. Set only on corner frames. */
  interiorAngle?: number;
}

/** Where a panel sits on its surface. `spin` is the rotation in the surface
 *  plane (0 = the panel's width runs along `u`); ignored for corner traps. */
export interface SurfacePose {
  u: number;
  v: number;
  spin: number;
}

export function sameSurface(a: SurfaceRef | null, b: SurfaceRef | null): boolean {
  if (!a || !b) return false;
  return a.kind === b.kind && a.index === b.index;
}

/** Stable string id for a surface — the key for `doc.surfaceColors` and for
 *  `WindowEntry.surface`.
 *
 *  The floor is a singleton and drops its index. The CEILING is indexed, but
 *  facet 0 deliberately serialises as the bare `ceiling` rather than
 *  `ceiling:0`: a flat roof is one facet, every document ever saved keys it
 *  that way, and those must keep loading. Only the extra facets a sloped roof
 *  adds (see `ceilingFacets` in @roomtreatment/viewport-3d) take a suffix. */
export function surfaceKey(ref: SurfaceRef): string {
  if (ref.kind === 'wall' || ref.kind === 'corner') return `${ref.kind}:${ref.index}`;
  if (ref.kind === 'ceiling' && ref.index > 0) return `ceiling:${ref.index}`;
  return ref.kind;
}

export function parseSurfaceKey(key: string): SurfaceRef | null {
  if (key === 'ceiling' || key === 'floor') return { kind: key, index: 0 };
  const parts = key.split(':');
  if (parts.length !== 2) return null;
  const [kind, idx] = parts;
  if (kind !== 'wall' && kind !== 'corner' && kind !== 'ceiling') return null;
  const index = Number(idx);
  if (!Number.isInteger(index) || index < 0) return null;
  // Exactly one spelling per surface, or the same surface ends up with two
  // entries in `surfaceColors` and a colour written through one is invisible
  // to code reading the other. So: no `ceiling:0` (facet 0 is the bare
  // `ceiling`, which is also how every already-saved room spells it), and no
  // non-canonical numbers like `wall:01` that `surfaceKey` would never emit.
  if (String(index) !== idx) return null;
  if (kind === 'ceiling' && index === 0) return null;
  return { kind, index };
}

/**
 * The colour a surface actually paints with, given the document's colour map.
 *
 * Everything resolves to its own key, except a CEILING FACET, which falls back
 * to facet 0 — the bare `ceiling` key — before giving up. That fallback is what
 * makes "paint the ceiling blue, then make it vaulted" keep a blue roof
 * instead of scattering the colour the moment the roof grows creases.
 *
 * Deliberately resolved here rather than broadcast into `surfaceColors` when
 * the profile changes. `packages/core` cannot see the geometry packages (they
 * depend on IT), so the reducer has no way to count facets and a mutation-time
 * broadcast would mean a second, drifting copy of the facet rule living in
 * core. Resolving on read also behaves better: it is order-independent, it
 * survives the profile changing back and forth without accumulating orphaned
 * keys, and it leaves "the ceiling colour" as something you can still set
 * globally until you deliberately override one slope.
 *
 * Returns undefined when nothing is set, so callers keep their own defaults.
 */
export function resolveSurfaceColor(
  colors: Record<string, string> | undefined,
  ref: SurfaceRef,
): string | undefined {
  if (!colors) return undefined;
  const own = colors[surfaceKey(ref)];
  if (own != null) return own;
  if (ref.kind === 'ceiling' && ref.index > 0) return colors.ceiling;
  return undefined;
}

/** The openings on one surface, in its own (u, v) frame. */
export function windowsOn(doc: EditorDoc, ref: SurfaceRef): WindowEntry[] {
  const key = surfaceKey(ref);
  return (doc.windows ?? []).filter((w) => w.surface === key);
}

/** Keep an opening inside its surface — no window half-way through a corner. */
export function clampWindow<T extends { u: number; v: number; w: number; h: number }>(
  frame: SurfaceFrame, win: T,
): T {
  const w = Math.max(0.2, Math.min(win.w, frame.uMax));
  const h = Math.max(0.2, Math.min(win.h, frame.vMax));
  return {
    ...win,
    w,
    h,
    u: Math.max(w / 2, Math.min(win.u, frame.uMax - w / 2)),
    v: Math.max(h / 2, Math.min(win.v, frame.vMax - h / 2)),
  };
}

// ── Surface construction ──────────────────────────────────────────

function inwardNormal(a: PolyPt, b: PolyPt, poly: PolyPt[]): THREE.Vector3 | null {
  const dx = b.x - a.x;
  const dz = b.y - a.y;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return null;
  const mx = (a.x + b.x) / 2;
  const mz = (a.y + b.y) / 2;
  // Both perpendiculars; the inward one has its probe point inside the room.
  const probe = Math.min(0.02, len / 4);
  const cands: [number, number][] = [
    [-dz / len, dx / len],
    [dz / len, -dx / len],
  ];
  for (const [nx, nz] of cands) {
    if (pointInPolygon({ x: mx + nx * probe, y: mz + nz * probe }, poly)) {
      return new THREE.Vector3(nx, 0, nz);
    }
  }
  return null;
}

/** Label each wall relative to how the LISTENER faces — "front wall" means the
 *  wall behind the speakers, which is the one the seat looks at. That's what
 *  the phrase means in every acoustics text, and it's what a user pictures.
 *  With no listener yet, fall back to the backend's fixed naming (+X = front)
 *  so labels still line up with solver output. */
function labelWalls(frames: SurfaceFrame[], doc: EditorDoc): void {
  // The inward normal of the wall the seat LOOKS AT — that wall is the front
  // wall. With no seat yet, the way one would face: the project's forward
  // (`facingYawRad`, else −Z toward the z = 0 wall), exactly what ADD_LISTENER
  // gives a new seat. The old fallback here, +X, named a side wall "Front" in
  // every room before its seat existed, so a TV added then went on it.
  const ry = doc.listener?.rotationY ?? doc.facingYawRad ?? Math.PI;
  // Facing: yaw 0 looks along +Z (the atan2(dirX, dirZ) convention). The
  // faced wall's normal points back at the seat, so it's the negated facing.
  const frontNormal = new THREE.Vector3(-Math.sin(ry), 0, -Math.cos(ry));
  // Facing f, the seat's left hand points along UP × f. The wall ON that side
  // faces back across the room, so ITS normal is −(UP × f) = UP × frontNormal.
  const leftNormal = new THREE.Vector3().crossVectors(UP, frontNormal).normalize();
  const counts: Record<string, number> = {};
  for (const f of frames) {
    const along = f.n.dot(frontNormal);
    const across = f.n.dot(leftNormal);
    const base =
      Math.abs(along) >= Math.abs(across)
        ? (along > 0 ? 'Front wall' : 'Back wall')
        : (across > 0 ? 'Left wall' : 'Right wall');
    counts[base] = (counts[base] ?? 0) + 1;
    f.label = counts[base] > 1 ? `${base} ${counts[base]}` : base;
  }
}

/** Axis-aligned bounds of the room footprint — the ceiling / floor extent. */
function polyBounds(poly: PolyPt[]) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of poly) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minZ) minZ = p.y;
    if (p.y > maxZ) maxZ = p.y;
  }
  return { minX, maxX, minZ, maxZ };
}

/** Every mounting surface in the room: one frame per wall, plus ceiling, floor,
 *  and one per vertical corner (for corner traps). */
export function roomSurfaces(doc: EditorDoc): SurfaceFrame[] {
  const poly = getRoomPolygon(doc);
  const surfaces = surfacesForPolygon(poly, doc.dims.H);
  const walls = surfaces.filter((s) => s.ref.kind === 'wall');
  labelWalls(walls, doc);
  // Corners borrow their names from the walls, so they're named second.
  for (const s of surfaces) {
    if (s.ref.kind !== 'corner') continue;
    s.label = cornerLabel(walls, (s.ref.index - 1 + poly.length) % poly.length, s.ref.index);
  }
  return surfaces;
}

/** The geometry half of `roomSurfaces`, without the listener-relative labels —
 *  so the room RENDERER can build its wall meshes from exactly the frames
 *  panels are mounted against. One definition of "where the walls are" means a
 *  painted wall and a panel's host surface can never drift apart. */
export function surfacesForPolygon(poly: PolyPt[], H: number): SurfaceFrame[] {
  const walls: SurfaceFrame[] = [];

  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < MIN_WALL_LEN) continue;
    const n = inwardNormal(a, b, poly);
    if (!n) continue;
    // u = UP × n keeps (u, v, n) right-handed regardless of polygon winding.
    const u = new THREE.Vector3().crossVectors(UP, n).normalize();
    // Anchor u = 0 at whichever endpoint lies "behind" +u.
    const av = new THREE.Vector3(a.x, 0, a.y);
    const bv = new THREE.Vector3(b.x, 0, b.y);
    const origin = bv.clone().sub(av).dot(u) >= 0 ? av : bv;
    walls.push({
      ref: { kind: 'wall', index: i },
      label: 'Wall',
      origin,
      u,
      v: UP.clone(),
      n,
      uMax: len,
      vMax: H,
    });
  }

  const { minX, maxX, minZ, maxZ } = polyBounds(poly);
  const surfaces: SurfaceFrame[] = [...walls];

  // Ceiling: (u, v, n) = (+X, +Z, −Y). Flat at H — a sloped ceiling profile
  // isn't a mountable surface in this editor yet.
  surfaces.push({
    ref: { kind: 'ceiling', index: 0 },
    label: 'Ceiling',
    origin: new THREE.Vector3(minX, H, minZ),
    u: new THREE.Vector3(1, 0, 0),
    v: new THREE.Vector3(0, 0, 1),
    n: new THREE.Vector3(0, -1, 0),
    uMax: maxX - minX,
    vMax: maxZ - minZ,
  });
  // Floor: (u, v, n) = (+X, −Z, +Y).
  surfaces.push({
    ref: { kind: 'floor', index: 0 },
    label: 'Floor',
    origin: new THREE.Vector3(minX, 0, maxZ),
    u: new THREE.Vector3(1, 0, 0),
    v: new THREE.Vector3(0, 0, -1),
    n: new THREE.Vector3(0, 1, 0),
    uMax: maxX - minX,
    vMax: maxZ - minZ,
  });

  // Vertical corners — the home of bass traps. The frame's normal is the
  // inward bisector; `u` is unused (a trap can only slide vertically).
  // Reflex / 270° vertices are not mounts — a bass trap needs interior < π.
  for (let i = 0; i < poly.length; i++) {
    if (!isConvexCorner(poly, i)) continue;
    const prev = poly[(i - 1 + poly.length) % poly.length];
    const cur = poly[i];
    const next = poly[(i + 1) % poly.length];
    const nA = inwardNormal(prev, cur, poly);
    const nB = inwardNormal(cur, next, poly);
    if (!nA || !nB) continue;
    const bis = nA.clone().add(nB);
    if (bis.lengthSq() < 1e-6) continue; // collinear — not a real corner
    bis.normalize();
    surfaces.push({
      ref: { kind: 'corner', index: i },
      // Named in `roomSurfaces`, once the walls have their labels.
      label: 'Corner',
      origin: new THREE.Vector3(cur.x, 0, cur.y),
      u: new THREE.Vector3().crossVectors(UP, bis).normalize(),
      v: UP.clone(),
      n: bis,
      uMax: 0,
      vMax: H,
      interiorAngle: interiorAngleAt(poly, i),
    });
  }

  return surfaces;
}

/** A corner is named after the two walls that meet there, so "front-left
 *  corner" always means the corner where the front wall meets the left wall.
 *  Naming it independently (by footprint quadrant, say) drifts out of step with
 *  the listener-relative wall labels and starts pointing at the wrong end of
 *  the room. Front / back leads, since that's the axis people orient by. */
function cornerLabel(walls: SurfaceFrame[], edgeA: number, edgeB: number): string {
  const short = (i: number) => walls
    .find((w) => w.ref.index === i)?.label.replace(/\s*wall\s*\d*$/i, '');
  const a = short(edgeA);
  const b = short(edgeB);
  if (!a || !b) return 'Corner';
  const [first, second] = a === 'Front' || a === 'Back' ? [a, b] : [b, a];
  return `${first}-${second.toLowerCase()} corner`;
}

export function findSurface(
  surfaces: SurfaceFrame[], ref: SurfaceRef | null,
): SurfaceFrame | null {
  if (!ref) return null;
  return surfaces.find((s) => sameSurface(s.ref, ref)) ?? null;
}

// ── Pose ⇄ world transform ────────────────────────────────────────

/** In-plane half-extents of a `size` panel spun by `spin` — how much room it
 *  needs along the surface's u and v axes. */
export function halfExtents(size: PanelSize, spin: number) {
  const c = Math.abs(Math.cos(spin));
  const s = Math.abs(Math.sin(spin));
  return {
    hu: (c * size.w + s * size.h) / 2,
    hv: (s * size.w + c * size.h) / 2,
  };
}

/** Surface pose → the doc's world `position` + `rotation`.
 *
 *  `position` is the panel's FLUSH centre (half its depth off the surface);
 *  the air gap is applied on top of it by the renderer and by the backend,
 *  both as a displacement along local +Z. */
export function poseToTransform(
  frame: SurfaceFrame, pose: SurfacePose, size: PanelSize,
): { position: Vec3; rotation: Vec3 } {
  if (frame.ref.kind === 'corner') {
    // Straddles the corner: the wedge apex sits exactly on the room's
    // corner point (frame.origin), yawed so local +Z (the inward bisector,
    // per cornerWedgeVertices) faces into the room. Matches the backend's
    // corner rule (yaw only — see
    // admin_room_treatment._placement_to_admin_box's corner branch).
    return {
      position: [frame.origin.x, pose.v, frame.origin.z],
      rotation: [0, Math.atan2(frame.n.x, frame.n.z), 0],
    };
  }
  const c = frame.origin
    .clone()
    .addScaledVector(frame.u, pose.u)
    .addScaledVector(frame.v, pose.v)
    .addScaledVector(frame.n, size.d / 2);
  const q = new THREE.Quaternion().setFromAxisAngle(frame.n, pose.spin);
  const x = frame.u.clone().applyQuaternion(q);
  const y = frame.v.clone().applyQuaternion(q);
  const m = new THREE.Matrix4().makeBasis(x, y, frame.n);
  const e = new THREE.Euler().setFromRotationMatrix(m, 'XYZ');
  return { position: [c.x, c.y, c.z], rotation: [e.x, e.y, e.z] };
}

/** World transform → surface pose, plus how far the panel currently sits OFF
 *  the surface (0 when properly glued; non-zero for a legacy free-floating
 *  panel that predates surface glue). */
export function transformToPose(
  frame: SurfaceFrame, position: Vec3, rotation: Vec3, size: PanelSize,
): { pose: SurfacePose; offset: number } {
  const p = new THREE.Vector3(position[0], position[1], position[2]);
  const rel = p.clone().sub(frame.origin);
  if (frame.ref.kind === 'corner') {
    // Ideal seat is the corner point itself (frame.origin) — any horizontal
    // drift at all, in any direction, means the trap isn't flush.
    const inward = rel.clone().setY(0);
    return {
      pose: { u: 0, v: position[1], spin: 0 },
      offset: inward.length(),
    };
  }
  const e = new THREE.Euler(rotation[0], rotation[1], rotation[2], 'XYZ');
  const m = new THREE.Matrix4().makeRotationFromEuler(e);
  const x = new THREE.Vector3().setFromMatrixColumn(m, 0);
  return {
    pose: {
      u: rel.dot(frame.u),
      v: rel.dot(frame.v),
      spin: Math.atan2(x.dot(frame.v), x.dot(frame.u)),
    },
    offset: rel.dot(frame.n) - size.d / 2,
  };
}

/** Room-clearance margin kept between a wall panel's near edge and an
 *  occupied corner, metres. A corner trap already straddles the corner
 *  cavity — this is on top of that, purely so the two never render flush
 *  against each other (floating-point exact-touch would be an ugly edge
 *  case, not a deliberate design). */
const CORNER_CLEARANCE_GAP_M = 0.02;

// The corner trap's three local footprint vertices — still the SINGLE source
// of truth shared by the visual mesh (`cornerPrismGeometry`), this module's
// wall-clearance math, and now the solver's footprint. The definition moved
// to @roomtreatment/core because it is pure math with no THREE dependency,
// and `panelToWire` needs it: api-client depends on `core` alone, so keeping
// the wedge here would have dragged `three` into the wire serializer.
// Re-exported because this is where every existing consumer imports it from.
export { cornerWedgeVertices };

/** How far a corner trap seated at either end of `frame` (a wall) reaches
 *  into that wall's own [0, uMax] range, measured from each end. 0 at an end
 *  with no corner trap. Rotates the trap's actual triangular footprint
 *  vertices by its own yaw and projects them onto `frame.u`, rather than
 *  approximating the shape as a box. */
function cornerClearance(frame: SurfaceFrame, doc: EditorDoc): { start: number; end: number } {
  let start = 0;
  let end = 0;
  const startPoint = frame.origin;
  const endPoint = frame.origin.clone().addScaledVector(frame.u, frame.uMax);
  for (const p of doc.panels) {
    if (p.shape !== 'corner') continue;
    const [cx, , cz] = p.position;
    // Corner traps only ever yaw about Y (poseToTransform's corner branch
    // sets rotation = [0, atan2(n.x, n.z), 0]), so a plain 2D rotation is
    // exact here — no need for the general Euler/Matrix4 machinery.
    const yaw = p.rotation[1];
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    // A corner trap belongs to a wall's end only if it's actually seated at
    // that corner point (in the floor plane) — cheap, physically-meaningful
    // gate that doesn't depend on inferSurface's approximate scoring.
    const nearStart = Math.hypot(cx - startPoint.x, cz - startPoint.z) < 1.0;
    const nearEnd = Math.hypot(cx - endPoint.x, cz - endPoint.z) < 1.0;
    if (!nearStart && !nearEnd) continue;
    for (const [lx, lz] of cornerWedgeVertices(p.size.w, interiorAngleForPanel(doc, p))) {
      const wx = cx + lx * cos + lz * sin;
      const wz = cz - lx * sin + lz * cos;
      const rel = (wx - frame.origin.x) * frame.u.x + (wz - frame.origin.z) * frame.u.z;
      if (nearStart) start = Math.max(start, rel + CORNER_CLEARANCE_GAP_M);
      if (nearEnd) end = Math.max(end, frame.uMax - rel + CORNER_CLEARANCE_GAP_M);
    }
  }
  return { start: Math.max(0, start), end: Math.max(0, end) };
}

/** Keep the panel's whole footprint on its surface. Walls clamp against the
 *  wall rectangle (and away from any corner trap occupying one of its two
 *  ends); ceiling / floor clamp against the real room polygon (so an
 *  L-room's notch stays empty) via the shared box clamp. */
export function clampPose(
  frame: SurfaceFrame, pose: SurfacePose, size: PanelSize, doc: EditorDoc,
): SurfacePose {
  if (frame.ref.kind === 'corner') {
    const hv = size.h / 2;
    return { u: 0, spin: 0, v: clamp(pose.v, hv, Math.max(hv, frame.vMax - hv)) };
  }
  const { hu, hv } = halfExtents(size, pose.spin);
  if (frame.ref.kind === 'wall') {
    const onWall = {
      ...pose,
      u: clamp(pose.u, hu, Math.max(hu, frame.uMax - hu)),
      v: clamp(pose.v, hv, Math.max(hv, frame.vMax - hv)),
    };
    const seated = clampToWallBounds(frame, avoidWindows(frame, onWall, hu, hv, doc), hu, hv);
    const { start, end } = cornerClearance(frame, doc);
    const loU = Math.max(hu, start + hu);
    const hiU = Math.max(loU, Math.min(frame.uMax - hu, frame.uMax - end - hu));
    return { ...seated, u: clamp(seated.u, loU, hiU) };
  }
  // Ceiling / floor — clamp in world XZ against the footprint polygon.
  const c = frame.origin
    .clone()
    .addScaledVector(frame.u, pose.u)
    .addScaledVector(frame.v, pose.v);
  const spun = new THREE.Vector3()
    .addScaledVector(frame.u, hu)
    .addScaledVector(frame.v, hv);
  const cl = clampBoxToRoom(
    c.x, c.z, Math.abs(spun.x), Math.abs(spun.z), getRoomPolygon(doc),
  );
  const rel = new THREE.Vector3(cl.x, c.y, cl.z).sub(frame.origin);
  return { ...pose, u: rel.dot(frame.u), v: rel.dot(frame.v) };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function clampToWallBounds(
  frame: SurfaceFrame, pose: SurfacePose, hu: number, hv: number,
): SurfacePose {
  return {
    ...pose,
    u: clamp(pose.u, hu, Math.max(hu, frame.uMax - hu)),
    v: clamp(pose.v, hv, Math.max(hv, frame.vMax - hv)),
  };
}

/** Slide a panel clear of any window it overlaps.
 *
 *  You cannot hang a panel over a window, so a plan that shows one isn't
 *  buildable. Rather than refusing the drag, push the panel out along whichever
 *  axis it has penetrated least — it reads as sliding around an obstacle. If a
 *  window is too big to get clear of, the push is abandoned rather than fought:
 *  no control should ever feel like it's wrestling the cursor. */
function avoidWindows(
  frame: SurfaceFrame, pose: SurfacePose, hu: number, hv: number, doc: EditorDoc,
): SurfacePose {
  // Doors are a wall opening exactly like windows — a panel cannot cover
  // either, so both are pushed clear the same way.
  const wins = [...windowsOn(doc, frame.ref), ...doorsOn(doc, frame.ref)];
  if (wins.length === 0) return pose;
  let { u, v } = pose;
  for (let pass = 0; pass < 2; pass++) {
    let moved = false;
    for (const w of wins) {
      const gapU = hu + w.w / 2 - Math.abs(u - w.u);
      const gapV = hv + w.h / 2 - Math.abs(v - w.v);
      if (gapU <= 0 || gapV <= 0) continue;          // clear of this one
      // Push along the axis with the shallower overlap, but only if there's
      // actually room on that side of the window.
      if (gapU <= gapV) {
        const to = u >= w.u ? u + gapU : u - gapU;
        if (to >= hu && to <= frame.uMax - hu) { u = to; moved = true; continue; }
        const alt = v >= w.v ? v + gapV : v - gapV;
        if (alt >= hv && alt <= frame.vMax - hv) { v = alt; moved = true; }
      } else {
        const to = v >= w.v ? v + gapV : v - gapV;
        if (to >= hv && to <= frame.vMax - hv) { v = to; moved = true; continue; }
        const alt = u >= w.u ? u + gapU : u - gapU;
        if (alt >= hu && alt <= frame.uMax - hu) { u = alt; moved = true; }
      }
    }
    if (!moved) break;
  }
  return { ...pose, u, v };
}

// ── Inference ─────────────────────────────────────────────────────

/** Which surface is this panel mounted on?
 *
 *  Derived from the panel's own position + rotation rather than stored on the
 *  entry, so it works uniformly for solver output, shared/legacy documents,
 *  and hand-placed panels — no schema migration, no field to keep in sync.
 *  Corner-shaped panels only ever match corner frames, and vice versa. */
export function inferSurface(
  panel: PanelEntry, surfaces: SurfaceFrame[],
): SurfaceFrame | null {
  const wantCorner = panel.shape === 'corner';
  const e = new THREE.Euler(panel.rotation[0], panel.rotation[1], panel.rotation[2], 'XYZ');
  const m = new THREE.Matrix4().makeRotationFromEuler(e);
  const facing = new THREE.Vector3().setFromMatrixColumn(m, 2); // local +Z

  let best: SurfaceFrame | null = null;
  let bestScore = -Infinity;
  for (const f of surfaces) {
    if ((f.ref.kind === 'corner') !== wantCorner) continue;
    const { pose, offset } = transformToPose(f, panel.position, panel.rotation, panel.size);
    // Facing alignment dominates (a wall and its opposite are far apart in
    // normal but can be similar in distance for a small room); standoff and
    // staying within the surface bounds break the remaining ties.
    let score = facing.dot(f.n) * 2 - Math.abs(offset);
    if (f.ref.kind !== 'corner') {
      const outU = Math.max(0, -pose.u, pose.u - f.uMax);
      const outV = Math.max(0, -pose.v, pose.v - f.vMax);
      score -= (outU + outV) * 0.75;
    }
    if (score > bestScore) {
      bestScore = score;
      best = f;
    }
  }
  return best;
}

/** Everything a caller needs about a panel's mounting, in one read. */
export interface PanelSurfaceState {
  surfaces: SurfaceFrame[];
  frame: SurfaceFrame | null;
  pose: SurfacePose;
  /** Distance the panel currently floats off its surface, metres. > ~1 cm
   *  means it predates surface glue and wants re-seating. */
  offset: number;
}

export function readPanelSurface(doc: EditorDoc, panel: PanelEntry): PanelSurfaceState {
  const surfaces = roomSurfaces(doc);
  const frame = inferSurface(panel, surfaces);
  if (!frame) {
    return { surfaces, frame: null, pose: { u: 0, v: 0, spin: 0 }, offset: 0 };
  }
  const { pose, offset } = transformToPose(frame, panel.position, panel.rotation, panel.size);
  return { surfaces, frame, pose, offset };
}

/** Re-seat a panel onto `frame`, keeping its relative placement where that
 *  makes sense: the same fractional position along the surface, and the same
 *  height when moving wall→wall. Used by the surface picker. */
export function moveToSurface(
  from: SurfaceFrame | null,
  to: SurfaceFrame,
  pose: SurfacePose,
  size: PanelSize,
  doc: EditorDoc,
): { position: Vec3; rotation: Vec3; pose: SurfacePose } {
  let next: SurfacePose;
  if (to.ref.kind === 'corner') {
    // Corner traps only ever come FROM a corner (shape gates the surface
    // options to corner-only), so preserve height across the switch just
    // like wall→wall does — only a brand-new panel with no prior frame
    // falls back to the default.
    next = { u: 0, v: from ? pose.v : Math.min(doc.dims.H / 2, 1.2), spin: 0 };
  } else if (!from || from.ref.kind === 'corner') {
    next = { u: to.uMax / 2, v: to.ref.kind === 'wall' ? Math.min(to.vMax - size.h / 2, 1.2) : to.vMax / 2, spin: 0 };
  } else {
    const uFrac = from.uMax > 1e-6 ? pose.u / from.uMax : 0.5;
    const wallToWall = from.ref.kind === 'wall' && to.ref.kind === 'wall';
    next = {
      u: uFrac * to.uMax,
      // Wall → wall keeps the mounting height (an ear-height panel stays at
      // ear height). Anything involving ceiling/floor re-centres instead,
      // because "height" doesn't survive the axis change.
      v: wallToWall ? pose.v : to.vMax / 2,
      spin: wallToWall ? pose.spin : 0,
    };
  }
  const clamped = clampPose(to, next, size, doc);
  return { ...poseToTransform(to, clamped, size), pose: clamped };
}

/** The wall frame sharing the corner at this wall's u = 0 (`side: 'start'`) or
 *  u = uMax end (`side: 'end'`). Lets a drag carry a panel around a corner
 *  onto the neighbouring wall instead of jamming against the edge. */
export function adjacentWall(
  surfaces: SurfaceFrame[], frame: SurfaceFrame, side: 'start' | 'end',
): SurfaceFrame | null {
  if (frame.ref.kind !== 'wall') return null;
  const at = side === 'start'
    ? frame.origin
    : frame.origin.clone().addScaledVector(frame.u, frame.uMax);
  for (const s of surfaces) {
    if (s.ref.kind !== 'wall' || sameSurface(s.ref, frame.ref)) continue;
    const start = s.origin;
    const end = s.origin.clone().addScaledVector(s.u, s.uMax);
    if (start.distanceToSquared(at) < 1e-4 || end.distanceToSquared(at) < 1e-4) return s;
  }
  return null;
}

/** The corner surface whose vertex is closest (in the floor plane) to world
 *  point `(x, z)`. Drives the corner-trap drag: as the cursor moves across
 *  the room, the trap snaps to whichever corner is nearest, the discrete
 *  equivalent of a wall panel sliding continuously along `u`. Returns null
 *  only if `surfaces` has no corner frames at all (no convex vertices). */
export function nearestCornerFrame(
  surfaces: SurfaceFrame[], x: number, z: number,
): SurfaceFrame | null {
  let best: SurfaceFrame | null = null;
  let bestDistSq = Infinity;
  for (const s of surfaces) {
    if (s.ref.kind !== 'corner') continue;
    const dx = s.origin.x - x;
    const dz = s.origin.z - z;
    const d = dx * dx + dz * dz;
    if (d < bestDistSq) {
      bestDistSq = d;
      best = s;
    }
  }
  return best;
}

/** Nearest point on `frame`'s bounded (u, v) rectangle to world `position` —
 *  the shared distance metric behind `nearestSurface`. A corner frame's
 *  rectangle degenerates to a vertical line at u=0 (uMax is always 0), so
 *  this naturally reduces to "nearest point on the corner edge" there,
 *  needing no separate corner case. */
function nearestPointOnSurface(frame: SurfaceFrame, position: THREE.Vector3): THREE.Vector3 {
  const rel = position.clone().sub(frame.origin);
  const u = Math.max(0, Math.min(frame.uMax, rel.dot(frame.u)));
  const v = Math.max(0, Math.min(frame.vMax, rel.dot(frame.v)));
  return frame.origin.clone().addScaledVector(frame.u, u).addScaledVector(frame.v, v);
}

/** The surface (matching `panel.shape`'s corner-vs-flat kind) whose bounded
 *  rectangle is nearest a DETACHED panel's current world position — used to
 *  re-attach it. Deliberately proximity-only rather than reusing
 *  `inferSurface`'s facing-based scoring: a detached panel's rotation is
 *  arbitrary (the user free-rotated it), so facing alignment isn't a
 *  meaningful signal for which surface it should land back on. */
export function nearestSurface(
  surfaces: SurfaceFrame[], panel: PanelEntry,
): SurfaceFrame | null {
  const wantCorner = panel.shape === 'corner';
  const p = new THREE.Vector3(panel.position[0], panel.position[1], panel.position[2]);
  let best: SurfaceFrame | null = null;
  let bestDistSq = Infinity;
  for (const f of surfaces) {
    if ((f.ref.kind === 'corner') !== wantCorner) continue;
    const d = p.distanceToSquared(nearestPointOnSurface(f, p));
    if (d < bestDistSq) {
      bestDistSq = d;
      best = f;
    }
  }
  return best;
}
